import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, ChevronRight, PlusCircle, Sparkles,
  Loader2, User, Briefcase, GraduationCap, Code2, Eye,
  Printer, LayoutTemplate, TrendingUp, Award, X,
} from "lucide-react";
import { toast } from "sonner";

// ── Constants ─────────────────────────────────────────────────────────────────
const ACTION_VERBS = new Set([
  "led","built","developed","designed","implemented","managed","created","launched",
  "improved","increased","reduced","delivered","achieved","architected","deployed",
  "optimized","scaled","automated","mentored","coordinated","streamlined","generated",
  "drove","established","spearheaded","negotiated","secured","founded","grew",
  "engineered","integrated","migrated","revamped","analyzed","collaborated","trained",
  "directed","supervised","executed","produced","transformed","accelerated","oversaw",
  "initiated","championed","shipped","owned","resolved","refactored","researched",
  "partnered","advised","influenced","enabled","facilitated","restructured","modernized",
]);

const INDUSTRIES = [
  "Software Engineering","Product Management","Data Science / ML","Finance / Banking",
  "Consulting","Marketing","Design / UX","Sales","Operations","Healthcare",
  "Legal","Education","Startups / VC","Other",
];

const STEPS = [
  { label: "Personal",   icon: User },
  { label: "Experience", icon: Briefcase },
  { label: "Education",  icon: GraduationCap },
  { label: "Skills",     icon: Code2 },
  { label: "Preview",    icon: Eye },
];

const EMPTY = {
  name: "", email: "", phone: "", location: "", linkedin: "", website: "",
  targetRole: "", industry: "", summary: "",
  experience: [{ company: "", title: "", start: "", end: "", current: false, bullets: [""] }],
  education:  [{ school: "", degree: "", field: "", year: "", gpa: "" }],
  skills: "",
  certifications: [{ name: "", issuer: "", year: "" }],
  projects: [{ name: "", description: "", tech: "", url: "" }],
  languages: "",
};

const token = () => localStorage.getItem("token");

async function aiPost(endpoint, body) {
  const res = await fetch(`/api${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "AI request failed");
  }
  return res.json();
}

// ── Scoring ───────────────────────────────────────────────────────────────────
function scoreResume(data) {
  const allBullets = data.experience.flatMap(e => e.bullets.filter(Boolean));
  const verbHits = allBullets.filter(b =>
    ACTION_VERBS.has(b.trim().split(/\s+/)[0]?.toLowerCase())
  ).length;
  const verbScore = allBullets.length > 0 ? Math.min(20, Math.round((verbHits / allBullets.length) * 20)) : 0;

  const quantHits = allBullets.filter(b =>
    /\d+\s*[%×x]|\$\s*\d|\d+\s*(million|billion|[kmKM]\b|percent|users?|customers?|ms\b)/i.test(b) || /\b\d{2,}\b/.test(b)
  ).length;
  const quantScore = allBullets.length > 0 ? Math.min(20, Math.round((quantHits / allBullets.length) * 22)) : 0;

  let comp = 0;
  if (data.name && data.email) comp += 4;
  if (data.summary?.trim().length > 30) comp += 4;
  if (data.experience.some(e => e.company && e.title)) comp += 5;
  if (data.education.some(e => e.school)) comp += 4;
  if (data.skills?.trim().length > 10) comp += 3;

  const jobs = data.experience.filter(e => e.company);
  const avgBullets = jobs.length
    ? data.experience.reduce((s, e) => s + e.bullets.filter(Boolean).length, 0) / jobs.length : 0;
  const bulletScore = Math.min(20, Math.round(
    avgBullets >= 3 && avgBullets <= 5 ? 20
      : avgBullets < 3 ? avgBullets * 6
      : Math.max(10, 20 - (avgBullets - 5) * 2)
  ));

  let extras = 0;
  if (data.certifications?.some(c => c.name)) extras += 8;
  if (data.projects?.some(p => p.name)) extras += 8;
  if (data.languages?.trim()) extras += 4;
  extras = Math.min(20, extras);

  return {
    total: verbScore + quantScore + comp + bulletScore + extras,
    dimensions: [
      { label: "Action Verbs",      score: verbScore,   tip: verbHits < allBullets.length ? "Start more bullets with strong verbs (Led, Built, Reduced…)" : null },
      { label: "Quantified Impact", score: quantScore,  tip: quantHits < Math.ceil(allBullets.length * 0.5) ? "Add numbers, % or $ to achievements" : null },
      { label: "Completeness",      score: comp,        tip: comp < 16 ? "Fill in summary, experience, education and skills" : null },
      { label: "Bullet Quality",    score: bulletScore, tip: avgBullets < 3 ? "Aim for 3–5 bullets per role" : avgBullets > 5 ? "Too many bullets — trim to 3–5" : null },
      { label: "Extras & Depth",    score: extras,      tip: extras < 16 ? "Add certifications, projects or languages" : null },
    ],
  };
}

// ── Resume Template Building Blocks ──────────────────────────────────────────
function ExpEntry({ exp, titleColor = "#0a0a0a", companyColor = "#555", dateColor = "#888", bulletColor = "#444" }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 2 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 12.5, color: titleColor }}>{exp.title}</span>
          {exp.company && <span style={{ fontSize: 12, color: companyColor }}> · {exp.company}</span>}
        </div>
        <span style={{ fontSize: 10.5, color: dateColor, whiteSpace: "nowrap", marginLeft: 8 }}>
          {[exp.start, exp.current ? "Present" : exp.end].filter(Boolean).join(" – ")}
        </span>
      </div>
      {exp.bullets.filter(Boolean).map((b, j) => (
        <div key={j} style={{ display: "flex", gap: 6, marginBottom: 3 }}>
          <span style={{ color: "#999", flexShrink: 0, lineHeight: 1.6 }}>•</span>
          <span style={{ fontSize: 11.5, color: bulletColor, lineHeight: 1.6 }}>{b.replace(/^[•·\-]\s*/, "")}</span>
        </div>
      ))}
    </div>
  );
}

function EduEntry({ edu, nameColor = "#0a0a0a", subColor = "#555", yearColor = "#888" }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontWeight: 700, fontSize: 12.5, color: nameColor }}>{edu.school}</span>
        <span style={{ fontSize: 10.5, color: yearColor }}>{edu.year}</span>
      </div>
      {(edu.degree || edu.field) && (
        <div style={{ fontSize: 11.5, color: subColor }}>
          {[edu.degree, edu.field].filter(Boolean).join(", ")}
          {edu.gpa ? ` · GPA: ${edu.gpa}` : ""}
        </div>
      )}
    </div>
  );
}

function CertEntry({ cert, color = "#444" }) {
  return (
    <div style={{ fontSize: 11.5, color, marginBottom: 4 }}>
      <span style={{ fontWeight: 600 }}>{cert.name}</span>
      {cert.issuer && <span style={{ color: "#666" }}> · {cert.issuer}</span>}
      {cert.year && <span style={{ color: "#888" }}> ({cert.year})</span>}
    </div>
  );
}

function ProjectEntry({ proj, urlColor = "#0055FF", bodyColor = "#444" }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div>
        <span style={{ fontWeight: 700, fontSize: 12.5, color: "#0a0a0a" }}>{proj.name}</span>
        {proj.url && (
          <span style={{ fontSize: 10.5, color: urlColor, marginLeft: 8 }}>{proj.url}</span>
        )}
      </div>
      {proj.description && (
        <div style={{ fontSize: 11.5, color: bodyColor, lineHeight: 1.6 }}>{proj.description}</div>
      )}
      {proj.tech && (
        <div style={{ fontSize: 10.5, color: "#888", marginTop: 2 }}>Stack: {proj.tech}</div>
      )}
    </div>
  );
}

// ── Template 1: Classic ───────────────────────────────────────────────────────
function ClassicSection({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "2.5px", textTransform: "uppercase", color: "#333", margin: "0 0 5px", paddingBottom: 4, borderBottom: "1px solid #ddd" }}>{title}</h2>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

function ClassicTemplate({ data }) {
  const contact = [data.email, data.phone, data.location, data.linkedin, data.website].filter(Boolean);
  return (
    <div id="resume-render" style={{ background: "#fff", fontFamily: '"Helvetica Neue", Arial, sans-serif', maxWidth: 720, margin: "0 auto", padding: "44px 52px", fontSize: 12, lineHeight: 1.5, boxShadow: "0 4px 32px rgba(0,0,0,0.15)" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "#0a0a0a", margin: "0 0 3px", letterSpacing: "-0.3px" }}>{data.name || "Your Name"}</h1>
        {data.targetRole && <div style={{ fontSize: 12.5, color: "#555", fontWeight: 600, marginBottom: 4 }}>{data.targetRole}</div>}
        {contact.length > 0 && <div style={{ fontSize: 10.5, color: "#666" }}>{contact.join(" · ")}</div>}
        <div style={{ marginTop: 10, height: 2, background: "#0a0a0a" }} />
      </div>
      {data.summary?.trim() && <ClassicSection title="Professional Summary"><p style={{ fontSize: 11.5, color: "#444", lineHeight: 1.65, margin: 0 }}>{data.summary}</p></ClassicSection>}
      {data.experience.some(e => e.company || e.title) && (
        <ClassicSection title="Experience">
          {data.experience.filter(e => e.company || e.title).map((exp, i) => <ExpEntry key={i} exp={exp} />)}
        </ClassicSection>
      )}
      {data.education.some(e => e.school) && (
        <ClassicSection title="Education">
          {data.education.filter(e => e.school).map((edu, i) => <EduEntry key={i} edu={edu} />)}
        </ClassicSection>
      )}
      {data.skills?.trim() && <ClassicSection title="Skills"><p style={{ fontSize: 11.5, color: "#444", margin: 0, lineHeight: 1.7 }}>{data.skills}</p></ClassicSection>}
      {data.certifications?.some(c => c.name) && (
        <ClassicSection title="Certifications">
          {data.certifications.filter(c => c.name).map((cert, i) => <CertEntry key={i} cert={cert} />)}
        </ClassicSection>
      )}
      {data.projects?.some(p => p.name) && (
        <ClassicSection title="Projects">
          {data.projects.filter(p => p.name).map((proj, i) => <ProjectEntry key={i} proj={proj} />)}
        </ClassicSection>
      )}
      {data.languages?.trim() && <ClassicSection title="Languages"><p style={{ fontSize: 11.5, color: "#444", margin: 0 }}>{data.languages}</p></ClassicSection>}
    </div>
  );
}

// ── Template 2: Modern ────────────────────────────────────────────────────────
function ModernSection({ title, children, accent = "#0055FF" }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ width: 3, height: 14, background: accent, flexShrink: 0 }} />
        <h2 style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: accent, margin: 0 }}>{title}</h2>
      </div>
      <div style={{ paddingLeft: 11, borderLeft: `1px solid ${accent}22` }}>{children}</div>
    </div>
  );
}

function ModernTemplate({ data }) {
  const accent = "#0055FF";
  const contact = [data.email, data.phone, data.location, data.linkedin, data.website].filter(Boolean);
  return (
    <div id="resume-render" style={{ background: "#fff", fontFamily: '"Helvetica Neue", Arial, sans-serif', maxWidth: 720, margin: "0 auto", padding: "44px 52px", fontSize: 12, lineHeight: 1.5, boxShadow: "0 4px 32px rgba(0,0,0,0.15)" }}>
      <div style={{ borderLeft: `4px solid ${accent}`, paddingLeft: 16, marginBottom: 26 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0a0a0a", margin: "0 0 3px", letterSpacing: "-0.3px" }}>{data.name || "Your Name"}</h1>
        {data.targetRole && <div style={{ fontSize: 12.5, color: accent, fontWeight: 600, marginBottom: 4 }}>{data.targetRole}</div>}
        {contact.length > 0 && <div style={{ fontSize: 10.5, color: "#666" }}>{contact.join(" · ")}</div>}
      </div>
      {data.summary?.trim() && <ModernSection title="Summary" accent={accent}><p style={{ fontSize: 11.5, color: "#444", lineHeight: 1.65, margin: 0 }}>{data.summary}</p></ModernSection>}
      {data.experience.some(e => e.company || e.title) && (
        <ModernSection title="Experience" accent={accent}>
          {data.experience.filter(e => e.company || e.title).map((exp, i) => <ExpEntry key={i} exp={exp} titleColor={accent} />)}
        </ModernSection>
      )}
      {data.education.some(e => e.school) && (
        <ModernSection title="Education" accent={accent}>
          {data.education.filter(e => e.school).map((edu, i) => <EduEntry key={i} edu={edu} nameColor={accent} />)}
        </ModernSection>
      )}
      {data.skills?.trim() && <ModernSection title="Skills" accent={accent}><p style={{ fontSize: 11.5, color: "#444", margin: 0, lineHeight: 1.7 }}>{data.skills}</p></ModernSection>}
      {data.certifications?.some(c => c.name) && (
        <ModernSection title="Certifications" accent={accent}>
          {data.certifications.filter(c => c.name).map((cert, i) => <CertEntry key={i} cert={cert} />)}
        </ModernSection>
      )}
      {data.projects?.some(p => p.name) && (
        <ModernSection title="Projects" accent={accent}>
          {data.projects.filter(p => p.name).map((proj, i) => <ProjectEntry key={i} proj={proj} urlColor={accent} />)}
        </ModernSection>
      )}
      {data.languages?.trim() && <ModernSection title="Languages" accent={accent}><p style={{ fontSize: 11.5, color: "#444", margin: 0 }}>{data.languages}</p></ModernSection>}
    </div>
  );
}

// ── Template 3: Executive ─────────────────────────────────────────────────────
function ExecutiveSection({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "2.5px", textTransform: "uppercase", color: "#0a0a0a", margin: "0 0 8px", paddingBottom: 5, borderBottom: "2px solid #0a0a0a" }}>{title}</h2>
      <div>{children}</div>
    </div>
  );
}

function ExecutiveTemplate({ data }) {
  const contact = [data.email, data.phone, data.location, data.linkedin, data.website].filter(Boolean);
  return (
    <div id="resume-render" style={{ background: "#fff", fontFamily: 'Georgia, "Times New Roman", serif', maxWidth: 720, margin: "0 auto", fontSize: 12, lineHeight: 1.55, boxShadow: "0 4px 32px rgba(0,0,0,0.15)" }}>
      <div style={{ background: "#0a0a0a", padding: "32px 52px 26px" }}>
        <h1 style={{ fontSize: 30, fontWeight: 700, color: "#fff", margin: "0 0 5px", letterSpacing: "0.3px" }}>{data.name || "Your Name"}</h1>
        {data.targetRole && <div style={{ fontSize: 12.5, color: "#D3FF24", fontWeight: 600, marginBottom: 5 }}>{data.targetRole}</div>}
        {contact.length > 0 && <div style={{ fontSize: 10.5, color: "#aaa", letterSpacing: "0.2px" }}>{contact.join("  ·  ")}</div>}
      </div>
      <div style={{ padding: "30px 52px" }}>
        {data.summary?.trim() && <ExecutiveSection title="Professional Summary"><p style={{ fontSize: 11.5, color: "#444", lineHeight: 1.7, margin: 0 }}>{data.summary}</p></ExecutiveSection>}
        {data.experience.some(e => e.company || e.title) && (
          <ExecutiveSection title="Professional Experience">
            {data.experience.filter(e => e.company || e.title).map((exp, i) => <ExpEntry key={i} exp={exp} />)}
          </ExecutiveSection>
        )}
        {data.education.some(e => e.school) && (
          <ExecutiveSection title="Education">
            {data.education.filter(e => e.school).map((edu, i) => <EduEntry key={i} edu={edu} />)}
          </ExecutiveSection>
        )}
        {data.skills?.trim() && <ExecutiveSection title="Core Competencies"><p style={{ fontSize: 11.5, color: "#444", margin: 0, lineHeight: 1.7 }}>{data.skills}</p></ExecutiveSection>}
        {data.certifications?.some(c => c.name) && (
          <ExecutiveSection title="Certifications">
            {data.certifications.filter(c => c.name).map((cert, i) => <CertEntry key={i} cert={cert} />)}
          </ExecutiveSection>
        )}
        {data.projects?.some(p => p.name) && (
          <ExecutiveSection title="Notable Projects">
            {data.projects.filter(p => p.name).map((proj, i) => <ProjectEntry key={i} proj={proj} urlColor="#0055FF" />)}
          </ExecutiveSection>
        )}
        {data.languages?.trim() && <ExecutiveSection title="Languages"><p style={{ fontSize: 11.5, color: "#444", margin: 0 }}>{data.languages}</p></ExecutiveSection>}
      </div>
    </div>
  );
}

// ── Template 4: Minimal ───────────────────────────────────────────────────────
function MinimalSection({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h2 style={{ fontSize: 9, fontWeight: 600, letterSpacing: "3px", textTransform: "uppercase", color: "#aaa", margin: "0 0 10px" }}>{title}</h2>
      <div>{children}</div>
    </div>
  );
}

function MinimalTemplate({ data }) {
  const contact = [data.email, data.phone, data.location, data.linkedin, data.website].filter(Boolean);
  return (
    <div id="resume-render" style={{ background: "#fff", fontFamily: '"Helvetica Neue", Arial, sans-serif', maxWidth: 720, margin: "0 auto", padding: "52px 60px", fontSize: 12, lineHeight: 1.6, boxShadow: "0 4px 32px rgba(0,0,0,0.15)" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 34, fontWeight: 300, color: "#0a0a0a", margin: "0 0 4px", letterSpacing: "-1px" }}>{data.name || "Your Name"}</h1>
        {data.targetRole && <div style={{ fontSize: 13, color: "#888", fontWeight: 400, marginBottom: 8 }}>{data.targetRole}</div>}
        {contact.length > 0 && <div style={{ fontSize: 10.5, color: "#999" }}>{contact.join(" · ")}</div>}
      </div>
      {data.summary?.trim() && <MinimalSection title="About"><p style={{ fontSize: 12, color: "#555", lineHeight: 1.75, margin: 0 }}>{data.summary}</p></MinimalSection>}
      {data.experience.some(e => e.company || e.title) && (
        <MinimalSection title="Experience">
          {data.experience.filter(e => e.company || e.title).map((exp, i) => <ExpEntry key={i} exp={exp} titleColor="#0a0a0a" companyColor="#888" bulletColor="#555" />)}
        </MinimalSection>
      )}
      {data.education.some(e => e.school) && (
        <MinimalSection title="Education">
          {data.education.filter(e => e.school).map((edu, i) => <EduEntry key={i} edu={edu} nameColor="#0a0a0a" subColor="#888" yearColor="#aaa" />)}
        </MinimalSection>
      )}
      {data.skills?.trim() && <MinimalSection title="Skills"><p style={{ fontSize: 11.5, color: "#555", margin: 0, lineHeight: 1.8 }}>{data.skills}</p></MinimalSection>}
      {data.certifications?.some(c => c.name) && (
        <MinimalSection title="Certifications">
          {data.certifications.filter(c => c.name).map((cert, i) => <CertEntry key={i} cert={cert} color="#555" />)}
        </MinimalSection>
      )}
      {data.projects?.some(p => p.name) && (
        <MinimalSection title="Projects">
          {data.projects.filter(p => p.name).map((proj, i) => <ProjectEntry key={i} proj={proj} urlColor="#0055FF" bodyColor="#555" />)}
        </MinimalSection>
      )}
      {data.languages?.trim() && <MinimalSection title="Languages"><p style={{ fontSize: 11.5, color: "#555", margin: 0 }}>{data.languages}</p></MinimalSection>}
    </div>
  );
}

const TEMPLATES = [
  { id: "classic",   label: "Classic",   desc: "ATS-optimized, clean",    Component: ClassicTemplate },
  { id: "modern",    label: "Modern",    desc: "Blue accent, bold",        Component: ModernTemplate },
  { id: "executive", label: "Executive", desc: "Dark header, formal",      Component: ExecutiveTemplate },
  { id: "minimal",   label: "Minimal",   desc: "Lightweight, spacious",    Component: MinimalTemplate },
];

// ── Score Panel ───────────────────────────────────────────────────────────────
function ScorePanel({ data }) {
  const { total, dimensions } = scoreResume(data);
  const grade = total >= 90 ? "A+" : total >= 80 ? "A" : total >= 70 ? "B+" : total >= 60 ? "B" : total >= 50 ? "C" : "D";
  const col = total >= 80 ? "#D3FF24" : total >= 60 ? "#60a5fa" : total >= 40 ? "#fb923c" : "#f87171";
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">/ ATS Score</div>
          <div className="text-xs text-neutral-400">How your resume stacks up across 5 dimensions</div>
        </div>
        <div className="text-center">
          <div className="font-display text-4xl font-black" style={{ color: col }}>{total}</div>
          <div className="text-xs text-neutral-500">/ 100 · Grade <span style={{ color: col }} className="font-bold">{grade}</span></div>
        </div>
      </div>
      <div className="space-y-3">
        {dimensions.map(({ label, score, tip }) => (
          <div key={label}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-neutral-400">{label}</span>
              <span className="font-mono text-white">{score}<span className="text-neutral-600">/20</span></span>
            </div>
            <div className="h-1.5 bg-white/5 w-full">
              <div className="h-full transition-all duration-700" style={{ width: `${(score / 20) * 100}%`, background: score >= 16 ? "#D3FF24" : score >= 10 ? "#60a5fa" : "#fb923c" }} />
            </div>
            {tip && <div className="text-[10px] text-neutral-600 mt-0.5">→ {tip}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shared Field ──────────────────────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, as: As = "input", rows, className = "", hint }) {
  const cls = "mt-1.5 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none";
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{label}</label>
      {As === "textarea"
        ? <textarea rows={rows || 4} value={value} onChange={onChange} placeholder={placeholder} className={`${cls} resize-none`} />
        : <input value={value} onChange={onChange} placeholder={placeholder} className={cls} />}
      {hint && <div className="text-[10px] text-neutral-600 mt-1">{hint}</div>}
    </div>
  );
}

function AIBtn({ onClick, loading, label = "AI Write", size = "sm" }) {
  return (
    <button onClick={onClick} disabled={loading}
      className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#D3FF24] border border-[#D3FF24]/30 px-2 py-1 hover:bg-[#D3FF24]/10 transition disabled:opacity-50 whitespace-nowrap`}>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {label}
    </button>
  );
}

// ── Bullet Feedback ───────────────────────────────────────────────────────────
function BulletFeedback({ value }) {
  if (!value || value.trim().split(/\s+/).length < 4) return null;
  const first = value.trim().split(/\s+/)[0]?.toLowerCase();
  const hasVerb = ACTION_VERBS.has(first);
  const hasMetric = /\d+[%×x]|\$\d|\b\d{2,}\b/i.test(value);
  if (hasVerb && hasMetric) return <div className="text-[10px] text-[#D3FF24] mt-0.5 px-1">✓ Strong bullet</div>;
  return (
    <div className="flex gap-3 text-[10px] text-neutral-600 mt-0.5 px-1">
      {!hasVerb && <span>→ Start with action verb (Led, Built, Reduced…)</span>}
      {hasVerb && !hasMetric && <span>→ Add a metric (%, $ or number)</span>}
    </div>
  );
}

// ── Step 1: Personal ──────────────────────────────────────────────────────────
function Personal({ data, set }) {
  const [genSum, setGenSum] = useState(false);
  const f = k => e => set({ ...data, [k]: e.target.value });

  const generateSummary = async () => {
    setGenSum(true);
    try {
      const res = await aiPost("/resume/generate-summary", {
        name: data.name, target_role: data.targetRole, industry: data.industry,
        experience: data.experience, skills: data.skills,
      });
      set({ ...data, summary: res.summary });
      toast.success("Summary generated");
    } catch (e) { toast.error(e.message); }
    finally { setGenSum(false); }
  };

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full Name *"   value={data.name}     onChange={f("name")}     placeholder="Jane Smith" />
        <Field label="Email *"       value={data.email}    onChange={f("email")}    placeholder="jane@example.com" />
        <Field label="Phone"         value={data.phone}    onChange={f("phone")}    placeholder="+1 (555) 000-0000" />
        <Field label="Location"      value={data.location} onChange={f("location")} placeholder="San Francisco, CA" />
        <Field label="LinkedIn URL"  value={data.linkedin} onChange={f("linkedin")} placeholder="linkedin.com/in/janesmith" />
        <Field label="Portfolio / Website" value={data.website}  onChange={f("website")}  placeholder="janesmith.dev" />
      </div>

      <div className="border-t border-white/10 pt-5">
        <div className="text-[10px] uppercase tracking-widest text-[#D3FF24] font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> Industry-Specific AI Tailoring
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Target Role" value={data.targetRole} onChange={f("targetRole")} placeholder="Senior Software Engineer" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Industry</label>
            <select value={data.industry} onChange={f("industry")}
              className="mt-1.5 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none text-white">
              <option value="">Select industry…</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>
        {(data.targetRole || data.industry) && (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-[#D3FF24]/70 border border-[#D3FF24]/15 bg-[#D3FF24]/[0.03] px-3 py-2">
            <Sparkles className="w-3 h-3 shrink-0" />
            AI will tailor keywords, action verbs, and phrasing for {data.targetRole || "your role"}{data.industry ? ` in ${data.industry}` : ""}.
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Professional Summary</label>
          <AIBtn onClick={generateSummary} loading={genSum} label="Generate with AI" />
        </div>
        <textarea
          rows={5} value={data.summary} onChange={f("summary")}
          placeholder="Results-driven engineer with 5+ years of experience building scalable systems…"
          className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none resize-none"
        />
        <div className="text-[10px] text-neutral-600 mt-1">3–4 sentences · 60–80 words · No "I" · Focus on value you deliver</div>
      </div>
    </div>
  );
}

// ── Step 2: Experience ────────────────────────────────────────────────────────
function Experience({ data, set }) {
  const [enhancing, setEnhancing] = useState({});

  const upd = (i, k, v) => set({ ...data, experience: data.experience.map((e, idx) => idx === i ? { ...e, [k]: v } : e) });
  const updBullet = (i, bi, v) => upd(i, "bullets", data.experience[i].bullets.map((b, idx) => idx === bi ? v : b));
  const addBullet = (i) => upd(i, "bullets", [...data.experience[i].bullets, ""]);
  const removeBullet = (i, bi) => upd(i, "bullets", data.experience[i].bullets.filter((_, idx) => idx !== bi));
  const addExp = () => set({ ...data, experience: [...data.experience, { company: "", title: "", start: "", end: "", current: false, bullets: [""] }] });
  const removeExp = (i) => set({ ...data, experience: data.experience.filter((_, idx) => idx !== i) });

  const enhanceBullet = async (i, bi) => {
    const bullet = data.experience[i].bullets[bi];
    if (!bullet.trim()) { toast.error("Write something first"); return; }
    const key = `${i}-${bi}`;
    setEnhancing(p => ({ ...p, [key]: true }));
    try {
      const res = await aiPost("/resume/enhance-bullet", {
        bullet, title: data.experience[i].title, company: data.experience[i].company,
        industry: data.industry, target_role: data.targetRole,
      });
      updBullet(i, bi, res.bullet);
      toast.success("Bullet enhanced");
    } catch (e) { toast.error(e.message); }
    finally { setEnhancing(p => ({ ...p, [key]: false })); }
  };

  return (
    <div className="space-y-8">
      {data.experience.map((exp, i) => (
        <div key={i} className="border border-white/10 p-6 relative">
          {data.experience.length > 1 && (
            <button onClick={() => removeExp(i)} className="absolute top-4 right-4 text-neutral-600 hover:text-red-400 transition">
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <Field label="Company *"   value={exp.company} onChange={e => upd(i, "company", e.target.value)} placeholder="Google" />
            <Field label="Job Title *" value={exp.title}   onChange={e => upd(i, "title",   e.target.value)} placeholder="Senior Engineer" />
            <Field label="Start Date"  value={exp.start}   onChange={e => upd(i, "start",   e.target.value)} placeholder="Jan 2022" />
            <Field label="End Date"    value={exp.end}     onChange={e => upd(i, "end",     e.target.value)} placeholder="Present" />
          </div>
          <label className="flex items-center gap-2 text-xs text-neutral-400 mb-5 cursor-pointer select-none">
            <input type="checkbox" checked={exp.current} onChange={e => upd(i, "current", e.target.checked)} className="accent-[#D3FF24]" />
            Currently working here
          </label>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Key Achievements</label>
              <div className="text-[10px] text-neutral-600">Aim for 3–5 per role · click ✦ to enhance any bullet</div>
            </div>
            {exp.bullets.map((b, bi) => (
              <div key={bi}>
                <div className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input value={b} onChange={e => updBullet(i, bi, e.target.value)}
                      placeholder="Describe what you did — AI can rewrite it into a powerful achievement bullet…"
                      className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#0055FF] outline-none" />
                    <BulletFeedback value={b} />
                  </div>
                  <div className="flex gap-1 mt-0.5 shrink-0">
                    <AIBtn onClick={() => enhanceBullet(i, bi)} loading={enhancing[`${i}-${bi}`]} label="✦" />
                    {exp.bullets.length > 1 && (
                      <button onClick={() => removeBullet(i, bi)} className="text-neutral-600 hover:text-red-400 px-1.5 py-1 border border-white/10 text-sm">×</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => addBullet(i)} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-[#D3FF24] transition mt-1">
              <PlusCircle className="w-3.5 h-3.5" /> Add bullet
            </button>
          </div>
        </div>
      ))}
      <button onClick={addExp} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white border border-white/10 px-4 py-2.5 hover:border-white/20 transition">
        <PlusCircle className="w-4 h-4" /> Add another position
      </button>
    </div>
  );
}

// ── Step 3: Education ─────────────────────────────────────────────────────────
function Education({ data, set }) {
  const upd = (i, k, v) => set({ ...data, education: data.education.map((e, idx) => idx === i ? { ...e, [k]: v } : e) });
  const addEdu = () => set({ ...data, education: [...data.education, { school: "", degree: "", field: "", year: "", gpa: "" }] });
  const removeEdu = (i) => set({ ...data, education: data.education.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-6">
      {data.education.map((edu, i) => (
        <div key={i} className="border border-white/10 p-6 relative">
          {data.education.length > 1 && (
            <button onClick={() => removeEdu(i)} className="absolute top-4 right-4 text-neutral-600 hover:text-red-400 transition">
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="School / University *" value={edu.school} onChange={e => upd(i, "school", e.target.value)} placeholder="MIT" className="sm:col-span-2" />
            <Field label="Degree"                value={edu.degree} onChange={e => upd(i, "degree", e.target.value)} placeholder="B.S." />
            <Field label="Field of Study"        value={edu.field}  onChange={e => upd(i, "field",  e.target.value)} placeholder="Computer Science" />
            <Field label="Graduation Year"       value={edu.year}   onChange={e => upd(i, "year",   e.target.value)} placeholder="2021" />
            <Field label="GPA (optional)"        value={edu.gpa}    onChange={e => upd(i, "gpa",    e.target.value)} placeholder="3.8 / 4.0" hint="Only include if 3.5+" />
          </div>
        </div>
      ))}
      <button onClick={addEdu} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white border border-white/10 px-4 py-2.5 hover:border-white/20 transition">
        <PlusCircle className="w-4 h-4" /> Add another degree
      </button>
    </div>
  );
}

// ── Step 4: Skills & Extras ───────────────────────────────────────────────────
function SkillsExtras({ data, set }) {
  const [suggesting, setSuggesting] = useState(false);

  const suggestSkills = async () => {
    setSuggesting(true);
    try {
      const res = await aiPost("/resume/suggest-skills", {
        target_role: data.targetRole, industry: data.industry, experience: data.experience,
      });
      set({ ...data, skills: res.skills });
      toast.success("Skills suggested — edit as needed");
    } catch (e) { toast.error(e.message); }
    finally { setSuggesting(false); }
  };

  const updCert = (i, k, v) => set({ ...data, certifications: data.certifications.map((c, idx) => idx === i ? { ...c, [k]: v } : c) });
  const addCert = () => set({ ...data, certifications: [...data.certifications, { name: "", issuer: "", year: "" }] });
  const removeCert = (i) => set({ ...data, certifications: data.certifications.filter((_, idx) => idx !== i) });

  const updProj = (i, k, v) => set({ ...data, projects: data.projects.map((p, idx) => idx === i ? { ...p, [k]: v } : p) });
  const addProj = () => set({ ...data, projects: [...data.projects, { name: "", description: "", tech: "", url: "" }] });
  const removeProj = (i) => set({ ...data, projects: data.projects.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-10">
      {/* Skills */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Skills</label>
          <AIBtn onClick={suggestSkills} loading={suggesting} label="Suggest Skills" />
        </div>
        <textarea rows={4} value={data.skills}
          onChange={e => set({ ...data, skills: e.target.value })}
          placeholder="React, Node.js, Python, SQL, AWS, Docker, System Design, REST APIs, TypeScript…"
          className="w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none resize-none"
        />
        <div className="text-[10px] text-neutral-600 mt-1">Comma-separated · Mix technical skills, tools, and relevant soft skills</div>
      </div>

      {/* Certifications */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-4 h-4 text-[#D3FF24]" strokeWidth={1.75} />
          <div className="text-sm font-semibold text-white">Certifications</div>
          <div className="text-[10px] text-neutral-600">Optional — highly valued by recruiters</div>
        </div>
        <div className="space-y-4">
          {data.certifications.map((cert, i) => (
            <div key={i} className="border border-white/10 p-5 relative">
              {data.certifications.length > 1 && (
                <button onClick={() => removeCert(i)} className="absolute top-3 right-3 text-neutral-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              )}
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Certification Name" value={cert.name}   onChange={e => updCert(i, "name",   e.target.value)} placeholder="AWS Solutions Architect" className="sm:col-span-2" />
                <Field label="Year"               value={cert.year}   onChange={e => updCert(i, "year",   e.target.value)} placeholder="2023" />
                <Field label="Issuing Org"        value={cert.issuer} onChange={e => updCert(i, "issuer", e.target.value)} placeholder="Amazon Web Services" className="sm:col-span-3" />
              </div>
            </div>
          ))}
          <button onClick={addCert} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-[#D3FF24] transition">
            <PlusCircle className="w-3.5 h-3.5" /> Add certification
          </button>
        </div>
      </div>

      {/* Projects */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#D3FF24]" strokeWidth={1.75} />
          <div className="text-sm font-semibold text-white">Projects</div>
          <div className="text-[10px] text-neutral-600">Optional — essential for recent grads & career changers</div>
        </div>
        <div className="space-y-4">
          {data.projects.map((proj, i) => (
            <div key={i} className="border border-white/10 p-5 relative">
              {data.projects.length > 1 && (
                <button onClick={() => removeProj(i)} className="absolute top-3 right-3 text-neutral-600 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Project Name"  value={proj.name}        onChange={e => updProj(i, "name",        e.target.value)} placeholder="E-Commerce Platform" />
                <Field label="URL (optional)" value={proj.url}        onChange={e => updProj(i, "url",         e.target.value)} placeholder="github.com/you/project" />
                <Field label="Tech Stack"    value={proj.tech}        onChange={e => updProj(i, "tech",        e.target.value)} placeholder="React, Node.js, PostgreSQL" className="sm:col-span-2" />
                <Field label="Description"   value={proj.description} onChange={e => updProj(i, "description", e.target.value)} placeholder="Built a full-stack marketplace handling 10K daily transactions…" as="textarea" rows={2} className="sm:col-span-2" />
              </div>
            </div>
          ))}
          <button onClick={addProj} className="flex items-center gap-1.5 text-xs text-neutral-500 hover:text-[#D3FF24] transition">
            <PlusCircle className="w-3.5 h-3.5" /> Add project
          </button>
        </div>
      </div>

      {/* Languages */}
      <div>
        <Field label="Languages (optional)" value={data.languages}
          onChange={e => set({ ...data, languages: e.target.value })}
          placeholder="English (Native), Spanish (Professional), Mandarin (Conversational)"
          hint="Only include if relevant — include proficiency level"
        />
      </div>
    </div>
  );
}

// ── Step 5: Preview ───────────────────────────────────────────────────────────
function Preview({ data }) {
  const [templateId, setTemplateId] = useState("classic");
  const [showScore, setShowScore]   = useState(true);
  const tpl = TEMPLATES.find(t => t.id === templateId);

  return (
    <div className="space-y-6">
      {/* Template selector */}
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3">
          <LayoutTemplate className="w-3.5 h-3.5 text-[#D3FF24]" /> Choose Template
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {TEMPLATES.map(t => (
            <button key={t.id} onClick={() => setTemplateId(t.id)}
              className={`p-3 border text-left transition ${templateId === t.id ? "border-[#D3FF24] bg-[#D3FF24]/[0.05]" : "border-white/10 hover:border-white/25 bg-[#0A0A0A]"}`}>
              <div className={`text-xs font-semibold mb-0.5 ${templateId === t.id ? "text-[#D3FF24]" : "text-white"}`}>{t.label}</div>
              <div className="text-[10px] text-neutral-600">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => window.print()}
          className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white border border-white/10 px-3 py-1.5 hover:border-white/20 transition">
          <Printer className="w-3.5 h-3.5" /> Print / Save PDF
        </button>
        <button onClick={() => setShowScore(s => !s)}
          className={`flex items-center gap-1.5 text-xs border px-3 py-1.5 transition ${showScore ? "border-[#D3FF24]/40 text-[#D3FF24] bg-[#D3FF24]/[0.06]" : "border-white/10 text-neutral-400 hover:text-white"}`}>
          <TrendingUp className="w-3.5 h-3.5" /> ATS Score
        </button>
      </div>

      {/* Score panel */}
      {showScore && <ScorePanel data={data} />}

      {/* Live template preview */}
      <div className="border border-white/10 overflow-y-auto max-h-[72vh]">
        <tpl.Component data={data} />
      </div>

      <div className="text-[10px] text-neutral-600 text-center">
        Print this page → "Save as PDF" in your browser's print dialog for a perfect PDF export
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ResumeNew() {
  const nav = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    const pf = location.state?.prefill;
    if (pf && !prefilled) {
      setData({
        ...EMPTY, ...pf,
        experience:     pf.experience?.length     ? pf.experience     : EMPTY.experience,
        education:      pf.education?.length       ? pf.education      : EMPTY.education,
        certifications: pf.certifications?.length  ? pf.certifications : EMPTY.certifications,
        projects:       pf.projects?.length        ? pf.projects       : EMPTY.projects,
      });
      setPrefilled(true);
    }
  }, [location.state, prefilled]);

  const stepViews = [
    <Personal     data={data} set={setData} />,
    <Experience   data={data} set={setData} />,
    <Education    data={data} set={setData} />,
    <SkillsExtras data={data} set={setData} />,
    <Preview      data={data} />,
  ];

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          body > * { display: none !important; }
          #resume-render { display: block !important; box-shadow: none !important; }
          #resume-render * { display: revert !important; }
          @page { margin: 0; size: A4; }
        }
      `}</style>

      <div className="px-10 py-10 max-w-4xl">
        <button onClick={() => nav("/app/resume")} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-8 transition">
          <ArrowLeft className="w-4 h-4" /> Back to Resume Builder
        </button>

        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Build Your Resume</h1>
        <p className="text-neutral-500 text-sm mb-6">AI-powered · ATS-optimized · Live preview in 4 professional templates</p>

        {prefilled && (
          <div className="flex items-center gap-3 border border-[#D3FF24]/30 bg-[#D3FF24]/5 px-4 py-3 mb-6 text-sm text-[#D3FF24]">
            <Sparkles className="w-4 h-4 shrink-0" />
            Form pre-filled from your imported profile — review each step and edit as needed.
          </div>
        )}

        {/* Step indicator */}
        <div className="flex items-center mb-10 overflow-x-auto border-b border-white/10">
          {STEPS.map(({ label, icon: Icon }, i) => {
            const active = i === step, done = i < step;
            return (
              <React.Fragment key={label}>
                <button onClick={() => setStep(i)}
                  className={`flex items-center gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition border-b-2 -mb-px ${
                    active ? "border-[#D3FF24] text-white"
                    : done  ? "border-white/20 text-neutral-400 hover:text-white"
                            : "border-transparent text-neutral-600 hover:text-neutral-400"}`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                  {done && <span className="w-1.5 h-1.5 rounded-full bg-[#D3FF24] ml-1" />}
                </button>
                {i < STEPS.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-neutral-700 shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step content */}
        <div className="mb-10">{stepViews[step]}</div>

        {/* Navigation */}
        <div className="flex items-center justify-between border-t border-white/10 pt-6">
          <button onClick={() => step === 0 ? nav("/app/resume") : setStep(s => s - 1)}
            className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition">
            <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < STEPS.length - 1 && (
            <button onClick={() => setStep(s => s + 1)}
              className="flex items-center gap-2 bg-premium text-white px-6 py-2.5 font-semibold uppercase tracking-widest text-xs hover:brightness-110 transition">
              {step === STEPS.length - 2 ? "Preview Resume" : "Continue"} <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
